<template>
  <div>
    <integration-tab-wrapper heading="CallRail Integration" logo-path="/images/callrail-icon.png" :is-onboarding-integration="isOnboardingIntegration">
      <template v-if="!isOnboardingIntegration" v-slot:buttons>
        <button-link
          v-if="callRailIntegration"
          class="is-danger"
          @click="deleteIntegration"
          :loading="isDisconnecting"
          :disabled="isDisconnecting"
        >
          Disconnect CallRail
        </button-link>
      </template>

      <section-page>
        <!--Start CallRail Integration if not already done-->
        <grid-row v-if="!callRailIntegration">
          <grid-column :size="5">
            <p class="integration_text">To integrate with your CallRail account, go to your CallRail profile page, click
              API keys under the security section, and create a v3 API key. Then copy and paste that API key here.</p>
            <p class="mt-100">
              <a
                href="https://help.forefrontcrm.com/knowledge-base/integrations/callrail/integrating-with-callrail-2/"
                target="_blank"
              >View Instructions</a></p>
          </grid-column>

          <grid-column :size="7">
            <form-field-input
              class="mb-100"
              label="CallRail API Key"
              v-model="integrationForm.api_key"
              :validation-errors="integrationValidationErrors.api_key"
            >
              <template v-slot:after>
                <button-default
                  class="ml-100"
                  :disabled="isSaving"
                  :loading="isSaving"
                  @click="createIntegration"
                >
                  Connect to CallRail
                </button-default>
              </template>
            </form-field-input>
          </grid-column>
        </grid-row>

        <!--If CallRail integration is already done-->
        <template v-else>
          <!--Step 1: Account-->
          <div class="flex flex-vertical-center step mt-100">
            <!--Label-->
            <p class="step--label">Step 1</p>

            <!--Account Name-->
            <p
              v-if="isAccountSet"
              class="step--complete"
            >Account set as {{ callRailIntegration.data.account_name }}</p>

            <!--Account Dropdown-->
            <div v-else class="flex flex-vertical-center flex-grow step--select">
              <p>Select Account:</p>

              <form-field-select
                class="flex-grow ml-100"
                v-model="accountForm.account_id"
                :validation-errors="accountValidationErrors.account_id"
                :loading="!accounts"
              >
                <option :value="null" disabled>Select Account</option>
                <option
                  v-for="account in accounts"
                  :key="account.id"
                  :value="account.id"
                >
                  {{ account.name }}
                </option>

                <template v-slot:after>
                  <button-default
                    class="ml-100"
                    :disabled="!accounts || isSaving"
                    :loading="isSaving"
                    @click="setAccount"
                  >
                    Connect to Account
                  </button-default>
                </template>
              </form-field-select>
            </div>
          </div>

          <!--Step 2: Company-->
          <div class="flex flex-vertical-center step">
            <p class="step--label">Step 2</p>

            <!--Waiting for Account-->
            <p v-if="!isAccountSet" class="step--waiting">Choose company after selecting your account</p>

            <!--Company Name-->
            <p
              v-else-if="isCompanySet"
              class="step--complete"
            >Company set as {{ callRailIntegration.data.company_name }}</p>

            <!--Company Dropdown-->
            <div v-else class="flex flex-vertical-center flex-grow step--select">
              <p>Select Company:</p>

              <form-field-select
                class="flex-grow ml-100"
                v-model="companyForm.company_id"
                :validation-errors="companyValidationErrors.company_id"
                :loading="!companies"
              >
                <option :value="null" disabled>Select Company</option>
                <option
                  v-for="company in companies"
                  :key="company.id"
                  :value="company.id"
                >
                  {{ company.name }}
                </option>

                <template v-slot:after>
                  <button-default
                    class="ml-200"
                    :disabled="!companies || isSaving"
                    :loading="isSaving"
                    @click="setCompany"
                  >
                    Connect to Company
                  </button-default>
                </template>
              </form-field-select>
            </div>
          </div>
          <div v-if="companyValidationErrors.company_name" style="margin-top: -0.65rem">
            <p
              v-for="error in companyValidationErrors.company_name"
              :key="error"
              class="help is-danger"
            >{{ error }}</p>
          </div>

          <!--Step 3: Webhooks-->
          <div class="flex flex-vertical-center step">
            <p class="step--label">Step 3</p>

            <!--Waiting for Account & Company-->
            <p v-if="!isAccountSet || !isCompanySet" class="step--waiting">Set webhooks inside CallRail after selecting
              your
              account and company</p>

            <!--Set Webhooks-->
            <p v-else class="step--complete flex">
              Copy Webhooks to your CallRail Account
              <button-link
                class="ml-100"
                @click="$refs.webhookInstructionModal.open()"
                size=""
              >View Webhook Instructions
              </button-link>
            </p>
          </div>

          <!--Step 4: Manage Numbers-->
          <div class="flex flex-vertical-center step">
            <p class="step--label">Step 4</p>

            <!--Waiting for Account & Company-->
            <p v-if="!isAccountSet || !isCompanySet" class="step--waiting">Your numbers will appear below after selecting
              your account and company</p>

            <!--Account & Company Set-->
            <p v-else class="step--complete">Manage how your CallRail numbers work in Forefront below</p>
          </div>

          <!--Resync Button-->
          <hr>
          <div class="flex flex-vertical-center mb-50">
            <button-outlined
              v-if="callRailIntegration"
              class="mr-100"
              :loading="isResyncing"
              :disabled="isResyncing"
              @click="resyncNumbers"
            >
              Resync Numbers
            </button-outlined>

            <div>
              <p class="mb-25">This will resync with CallRail so new numbers show and deleted numbers are removed from
                your Forefront account. Any deleted numbers will be removed from attached campaigns/follow up
                sequences.</p>
            </div>
          </div>
        </template>

        <modal-container>
          <modal-default
            ref="webhookInstructionModal"
            title="Webhook Instructions"
            is-double-column
          >
            <p class="integration_text mb-100">Go to Settings in CallRail and select Manage under Integrations. Select
              'Webhooks' from the list of integrations and paste the following webhooks into the appropriate
              sections.</p>

            <!--Pre Call-->
            <div class="mb-100">
              <div class="flex flex-between flex-vertical-center">
                <p class="webhook--heading">Pre-Call</p>

                <button-outlined
                  @click="$utils.copyToClipboard(webhookUrls.incomingPreCall)"
                >Copy Pre-Call URL
                </button-outlined>
              </div>

              <p class="webhook--description">Used to create a lead when your CallRail number receives a call.</p>
              <p class="webhook--link">{{ webhookUrls.incomingPreCall }}</p>
            </div>

            <!--Post Call-->
            <div class="mb-100">
              <div class="flex flex-between flex-vertical-center">
                <p class="webhook--heading">Post-Call</p>

                <button-outlined
                  @click="$utils.copyToClipboard(webhookUrls.incomingPostCall)"
                >Copy Post-Call URL
                </button-outlined>
              </div>

              <p class="webhook--description">Used to create a communication log after your incoming CallRail call is
                over.</p>
              <p class="webhook--link">{{ webhookUrls.incomingPostCall }}</p>
            </div>

            <!--Outbound Post Call-->
            <div class="mb-100">
              <div class="flex flex-between flex-vertical-center">
                <p class="webhook--heading">Outbound Post-Call</p>

                <button-outlined
                  @click="$utils.copyToClipboard(webhookUrls.outgoingPostCall)"
                >Copy Outbound Post-Call URL
                </button-outlined>
              </div>

              <p class="webhook--description">Used to create a communication log after your outgoing CallRail call is
                over.</p>
              <p class="webhook--link">{{ webhookUrls.outgoingPostCall }}</p>
            </div>

            <!--Text Message Sent-->
            <div class="mb-100">
              <div class="flex flex-between flex-vertical-center">
                <p class="webhook--heading">Text Message Sent</p>

                <button-outlined
                  @click="$utils.copyToClipboard(webhookUrls.outgoingText)"
                >Copy Text Message Sent URL
                </button-outlined>
              </div>

              <p class="webhook--description">Used to create a communication log when you send a text via CallRail.</p>
              <p class="webhook--link">{{ webhookUrls.outgoingText }}</p>
            </div>

            <!--Text Message Received-->
            <div class="mb-100">
              <div class="flex flex-between flex-vertical-center">
                <p class="webhook--heading">Text Message Received</p>

                <button-outlined
                  @click="$utils.copyToClipboard(webhookUrls.incomingText)"
                >Copy Text Message Received URL
                </button-outlined>
              </div>

              <p class="webhook--description">Used to create a communication log when you receive a text via CallRail.</p>
              <p class="webhook--link">{{ webhookUrls.incomingText }}</p>
            </div>
          </modal-default>
        </modal-container>
      </section-page>
    </integration-tab-wrapper>

    <phone-numbers-settings
      ref="phoneNumberSettings"
      title="CallRail Numbers"
      :type="callRailType"
    ></phone-numbers-settings>
  </div>
</template>

<script>
import IntegrationBackButton from "../components/IntegrationBackButton.vue";
import PhoneNumbersSettings from "../components/PhoneNumbers.vue";
import {ENUMS} from "../../../services/enums";
import IntegrationTabWrapper from "../components/IntegrationTabWrapper.vue";

export default {
  name: "IntegrationCallRail",
  components: {IntegrationTabWrapper, PhoneNumbersSettings, IntegrationBackButton},
  props: {
    isOnboardingIntegration: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isSaving: false,
      isDisconnecting: false,
      isResyncing: false,
      accounts: undefined,
      companies: undefined,
      integrationForm: {
        market_id: this.$store.getters.market.id,
        api_key: "",
      },
      accountForm: {
        integration_id: null,
        account_id: null,
        account_name: null,
      },
      companyForm: {
        integration_id: null,
        company_id: null,
        company_name: null,
        status_id: null
      },
      integrationValidationErrors: {},
      accountValidationErrors: {},
      companyValidationErrors: {},
    };
  },
  computed: {
    callRailType() {
      return ENUMS.IntegrationTypeEnum.CallRail.value;
    },
    market() {
      return this.$store.getters.market;
    },
    callRailIntegration() {
      return this.$store.getters.callRailIntegration;
    },
    isAccountSet() {
      return this.callRailIntegration && !!this.callRailIntegration.token.account_id;
    },
    isCompanySet() {
      return this.callRailIntegration && !!this.callRailIntegration.token.company_id;
    },
    webhookUrls() {
      if (this.callRailIntegration) {
        const apiKey = this.callRailIntegration.data.flippilot_api_key;
        const baseUrl = process.env.MIX_BASE_API_URL + "webhooks/phone_service_integrations/call-rail";

        return {
          incomingPreCall: baseUrl + "/pre_incoming_call/" + apiKey,
          incomingPostCall: baseUrl + "/post_incoming_call/" + apiKey,
          outgoingPostCall: baseUrl + "/post_outgoing_call/" + apiKey,
          incomingText: baseUrl + "/incoming_text/" + apiKey,
          outgoingText: baseUrl + "/outgoing_text/" + apiKey,
        };
      }

      return {
        incomingPreCall: null,
        incomingPostCall: null,
        outgoingPostCall: null,
        incomingText: null,
        outgoingText: null,
      };
    }
  },
  mounted() {
    if (this.callRailIntegration && !this.isAccountSet) {
      this.loadAccountList();
    }
    if (this.isAccountSet && !this.isCompanySet) {
      this.loadCompanyList();
    }
  },
  methods: {
    loadAccountList() {
      this.$api.callRailIntegrations.getCallRailAccountList(this.market.id).then((response) => {
        this.accounts = response.data.accounts;
      });
    },
    loadCompanyList() {
      this.$api.callRailIntegrations.getCallRailCompanyList(this.market.id).then((response) => {
        this.companies = response.data;
      });
    },
    createIntegration() {
      this.isSaving = true;

      this.$api.callRailIntegrations.createCallRailIntegration(this.integrationForm).then(() => {
        this.integrationValidationErrors = {};
        this.loadAccountList();
      }, (error) => {
        if (error.response.status === 422) this.integrationValidationErrors = error.response.data.errors;
      }).finally(() => {
        this.isSaving = false;
      });
    },
    setAccount() {
      this.isSaving = true;
      this.accountForm.integration_id = this.callRailIntegration.id;
      this.accountForm.account_name = this.accounts.find(a => a.id === this.accountForm.account_id).name;

      this.$api.callRailIntegrations.setCallRailAccount(this.accountForm).then(() => {
        this.accountValidationErrors = {};
        this.loadCompanyList();
      }, (error) => {
        if (error.response.status === 422) this.accountValidationErrors = error.response.data.errors;
      }).finally(() => {
        this.isSaving = false;
      });
    },
    setCompany() {
      this.isSaving = true;
      this.companyForm.integration_id = this.callRailIntegration.id;
      this.companyForm.company_name = this.companies.find(a => a.id === this.companyForm.company_id).name;
      this.companyForm.status_id = (this.$store.getters["pipeline/contactingStatuses"].length !== 0)
        ? this.$store.getters["pipeline/contactingStatuses"][0].id
        : this.$store.getters["pipeline/activeStatuses"][0].id;

      this.$api.callRailIntegrations.setCallRailCompany(this.companyForm).then(() => {
        this.accountValidationErrors = {};
        this.$refs.phoneNumberSettings.getNumbers();
      }, (error) => {
        if (error.response.status === 422) this.accountValidationErrors = error.response.data.errors;
      }).finally(() => {
        this.isSaving = false;
      });
    },
    resyncNumbers() {
      this.isResyncing = true;
      const statusId = (this.$store.getters["pipeline/contactingStatuses"].length !== 0)
        ? this.$store.getters["pipeline/contactingStatuses"][0].id
        : this.$store.getters["pipeline/activeStatuses"][0].id;

      this.$api.callRailIntegrations.resync({
        integration_id: this.$store.getters.callRailIntegration.id,
        status_id: statusId
      }).finally(() => {
        this.$buefy.toast.open({message: "CallRail numbers have been synced", type: "is-success"});
        this.isResyncing = false;
      });
    },
    deleteIntegration() {
      this.$utils.confirm(
        "Are you sure you want to <strong>disconnect</strong> CallRail? This will remove phone numbers from all your Campaigns and Follow Up Sequences. Follow up sequences that have text message steps will no longer be sent.",
        "Disconnect CallRail Integration"
      )
        .then(() => {
          this.isDisconnecting = true;

          this.$api.integrations.deleteIntegration(this.callRailIntegration.id)
            .then(() => {
              this.$buefy.toast.open({
                message: "CallRail Account Disconnected",
                type: "is-success"
              });
              this.$refs.phoneNumberSettings.getNumbers();
            })
            .finally(() => {
              this.isDisconnecting = false;
            });
        });
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.integration_text {
  line-height: 1.3;
}

.step {
  margin-bottom: 20px;

  &--label {
    width: 90px;
    font-size: 1.125rem;
    color: $text-light;
    text-transform: uppercase;
  }

  &--select {
    max-width: 600px;
  }

  &--waiting {
    color: $text-light;
    font-style: italic;
  }

  &--complete {
    font-size: 1.25rem;

    span {
      color: $text-muted;
    }
  }
}

.webhook {

  &--heading {
    font-size: 1.25rem;
    font-weight: bold;
  }

  &--description {
    margin-top: 6px;
    padding-left: 12px;
    line-height: 1.3;
  }

  &--link {
    margin-top: 6px;
    padding-left: 12px;
    font-size: 0.75rem;
    font-style: italic;
    line-height: 1.3;
  }
}
</style>
